/**
 * Client-side SVG template validation + section derivation.
 *
 * Mirrors the BE's INVALID_SVG_TEMPLATE (422) rules so the admin gets fast
 * feedback before upload. The BE re-validates and is the source of truth; this
 * just catches obvious problems early and lets the form preview the regions.
 *
 * BE rules (per the SVG Templates API): must have a viewBox; must have at least
 * one `data-section`; section ids must be unique + snake_case; no `<script>`,
 * inline `on*` handlers, or `javascript:` URIs.
 */

const SNAKE_CASE_RE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

/**
 * Parse + validate raw SVG markup.
 * @returns {{ ok: boolean, errors: string[], sections: {id:string,label:string}[] }}
 */
export function validateSvgMarkup(raw) {
  const errors = [];
  const text = String(raw ?? '');

  if (!text.trim()) {
    return { ok: false, errors: ['File is empty.'], sections: [] };
  }

  let doc;
  try {
    doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  } catch {
    return { ok: false, errors: ['Could not parse the file as SVG.'], sections: [] };
  }

  // DOMParser reports XML errors via a <parsererror> node.
  if (doc.querySelector('parsererror')) {
    return { ok: false, errors: ['The file is not well-formed SVG/XML.'], sections: [] };
  }

  const svg = doc.querySelector('svg');
  if (!svg) {
    return { ok: false, errors: ['No <svg> root element found.'], sections: [] };
  }

  if (!svg.getAttribute('viewBox')) {
    errors.push('The <svg> must have a viewBox attribute.');
  }

  // Scripts / event handlers / javascript: URIs.
  if (doc.querySelector('script')) errors.push('Remove <script> elements.');
  const hasInlineHandler = Array.from(doc.querySelectorAll('*')).some((el) =>
    Array.from(el.attributes).some((a) => /^on/i.test(a.name) || /javascript:/i.test(a.value)),
  );
  if (hasInlineHandler) errors.push('Remove inline event handlers (on*) and javascript: URIs.');

  // data-section regions.
  const regionEls = Array.from(doc.querySelectorAll('[data-section]'));
  if (regionEls.length === 0) {
    errors.push('No interactive regions found — add data-section="<id>" to clickable groups.');
  }

  const seen = new Set();
  const sections = [];
  for (const el of regionEls) {
    const id = el.getAttribute('data-section');
    if (!id) continue;
    if (!SNAKE_CASE_RE.test(id)) errors.push(`Region id "${id}" must be lowercase snake_case.`);
    if (seen.has(id)) { errors.push(`Duplicate region id "${id}".`); continue; }
    seen.add(id);
    sections.push({ id, label: deriveLabel(id) });
  }

  return { ok: errors.length === 0, errors, sections };
}

/** "hot_blast_stove" -> "Hot Blast Stove" */
export function deriveLabel(id) {
  return String(id)
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
